import { useMemo } from 'react';

export const PACKS = {
  free: {
    id:            'free',
    name:          'Gratuit',
    color:         '#6b7280',
    gradient:      'linear-gradient(135deg, #4b5563, #6b7280)',
    maxProducts:   3,
    maxImages:     1,
    monthlyPrice:  0,
    yearlyPrice:   0,
    features: [
      '3 produits maximum',
      '1 image par produit',
      'Catalogue de base',
    ],
  },
  starter: {
    id:            'starter',
    name:          'Starter',
    color:         '#2563eb',
    gradient:      'linear-gradient(135deg, #1d4ed8, #3b82f6)',
    maxProducts:   25,
    maxImages:     5,
    monthlyPrice:  500,
    yearlyPrice:   3200,
    features: [
      '25 produits',
      '5 images par produit',
      'Catégories personnalisées',
      'Badge "Boutique"',
    ],
  },
  premium: {
    id:            'premium',
    name:          'Premium',
    color:         '#7c3aed',
    gradient:      'linear-gradient(135deg, #6d28d9, #8b5cf6)',
    maxProducts:   75,
    maxImages:     10,
    monthlyPrice:  800,
    yearlyPrice:   6300,
    features: [
      '75 produits',
      '10 images par produit',
      'Catégories personnalisées',
      'Badge "Premium"',
      'Lien WhatsApp direct',
    ],
  },
  pro: {
    id:            'pro',
    name:          'Pro',
    color:         '#d97706',
    gradient:      'linear-gradient(135deg, #b45309, #f59e0b)',
    maxProducts:   Infinity,
    maxImages:     Infinity,
    monthlyPrice:  1100,
    yearlyPrice:   8700,
    features: [
      'Produits illimités',
      'Images illimitées',
      'Catégories personnalisées',
      'Badge "Pro"',
      'Lien WhatsApp direct',
      'Avis clients',
    ],
  },
};

export const useShopPack = (shop) => {
  return useMemo(() => {
    if (!shop) return { pack: PACKS.free, isActive: false, isExpired: false };

    const packId = shop.pack || 'free';
    const pack   = PACKS[packId] || PACKS.free;

    // Parse packExpiry — gère tous les formats Firebase possibles
    const expiry = (() => {
      const raw = shop.packExpiry;
      if (!raw) return null;
      if (raw?.toDate) return raw.toDate();                    // Timestamp Firebase SDK
      if (typeof raw === 'string') return new Date(raw);       // string ISO (Worker REST)
      if (typeof raw === 'object' && raw.seconds != null) {    // {seconds, nanoseconds} sérialisé
        return new Date(raw.seconds * 1000);
      }
      return null;
    })();

    // packExpiry null + pack non-free = assigné manuellement sans date → actif indéfiniment
    const isActive  = packId === 'free'
      || expiry === null
      || expiry.getTime() > Date.now();
    const isExpired = packId !== 'free'
      && expiry !== null
      && expiry.getTime() <= Date.now();

    const effectivePack = isActive ? pack : PACKS.free;

    return {
      pack:          effectivePack,
      rawPack:       pack,
      isActive,
      isExpired,
      packId,
      expiry,
      maxProducts:   effectivePack.maxProducts,
      maxImages:     effectivePack.maxImages,
      canAddProduct: (count) => count < effectivePack.maxProducts,
      canAddImage:   (count) => effectivePack.maxImages === Infinity || count < effectivePack.maxImages,
      hasWhatsApp:   ['premium', 'pro'].includes(packId) && isActive,
      hasReviews:    packId === 'pro' && isActive,
      hasBoutique:   true,
    };
  }, [shop]);
};

export const formatPackExpiry = (expiry) => {
  if (!expiry) return null;
  const diff = expiry.getTime() - Date.now();
  if (diff <= 0) return 'Expiré';
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  if (days === 1) return 'Expire demain';
  if (days <= 7)  return `Expire dans ${days} jours`;
  if (days <= 30) return `Expire dans ${Math.ceil(days / 7)} semaines`;
  return `Expire dans ${Math.ceil(days / 30)} mois`;
};